import { useState, useEffect, useRef } from 'react';
import '../Assests/Leave_balance.css';
import { addLeave, getLeavesByProfile, getLeaves } from '../Db_Connections/leavesApi';

export default function LeaveBalance() {
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [leaves, setLeaves] = useState([]);

  const user = JSON.parse(sessionStorage.getItem('auth'));
  const isAdmin = user?.role === 'Admin';

  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: ''
  });

  const leaveQuota = {
    'Casual Leave': 12,
    'Sick Leave': 12
  };

  // --- DATA LOAD FUNCTION ---
  const loadLeaves = async () => {
    if (!user) return;

    // ✅ Admin -> all leaves, User -> only own leaves
    const result = isAdmin ? await getLeaves() : await getLeavesByProfile(user.id);
    if (result?.data) setLeaves(result.data);
  };

  // ✅ Guard: API only once
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to calculate booked days (for USER)
  const getBookedCount = (type) => {
    return leaves
      .filter(l => l.leave_type === type && l.status !== 'Rejected')
      .reduce((acc, curr) => acc + Number(curr.total_days), 0);
  };

  const leaveTypes = [
    { name: 'Casual Leave', icon: '🏖️', total: 12, color: '#e3f2fd' },
    { name: 'Sick Leave', icon: '🏥', total: 12, color: '#f3e5f5' }
  ];

  // ===== ADMIN: Employee-wise remaining summary =====
  const buildEmployeeSummary = () => {
    const map = {};

    for (const l of leaves) {
      const employeeName = l?.profiles
        ? `${l.profiles.first_name || ''} ${l.profiles.last_name || ''}`.trim()
        : `Employee (${l.profile_id})`;

      if (!map[employeeName]) {
        map[employeeName] = {
          name: employeeName,
          used: { 'Casual Leave': 0, 'Sick Leave': 0 }
        };
      }

      if (l.status !== 'Rejected') {
        map[employeeName].used[l.leave_type] =
          (map[employeeName].used[l.leave_type] || 0) + Number(l.total_days || 0);
      }
    }

    return Object.values(map).map((row) => ({
      name: row.name,
      casualUsed: row.used['Casual Leave'] || 0,
      sickUsed: row.used['Sick Leave'] || 0,
      casualLeft: (leaveQuota['Casual Leave'] || 12) - (row.used['Casual Leave'] || 0),
      sickLeft: (leaveQuota['Sick Leave'] || 12) - (row.used['Sick Leave'] || 0),
    })).sort((a, b) => (b.casualUsed + b.sickUsed) - (a.casualUsed + a.sickUsed));
  };

  // --- HANDLERS (User only) ---
  const handleApplyClick = (type) => {
    if (isAdmin) return; // ✅ admin cannot apply
    setSelectedType(type);
    setShowModal(true);
  };

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (isAdmin) return;

    if (!formData.startDate || !formData.endDate) {
      alert("Select Dates");
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end < start) {
      alert("End Date cannot be before Start Date");
      return;
    }

    const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    const { error } = await addLeave({
      profile_id: user.id,
      leave_type: selectedType,
      start_date: formData.startDate,
      end_date: formData.endDate,
      total_days: diffDays,
      reason: formData.reason
    });

    if (!error) {
      alert("Applied Successfully");
      setShowModal(false);
      setFormData({ startDate: '', endDate: '', reason: '' });
      loadLeaves();
    } else {
      alert(error.message);
    }
  };

  // ==================== RENDER ====================
  return (
    <>
      <div className="leave-balance-container">
        {/* ✅ ADMIN VIEW: show employee wise remaining */}
        {isAdmin ? (
          <div className="leave-list">
            {buildEmployeeSummary().map((emp) => (
              <div className="leave-item" key={emp.name}>
                <div className="leave-name" style={{ fontWeight: 800 }}>
                  {emp.name}
                </div>

                <div className="leave-info-section">
                  <div className="leave-info-label">Casual Left</div>
                  <div className={`leave-info-value ${emp.casualLeft > 0 ? 'green' : 'gray'}`}>
                    {emp.casualLeft} days
                  </div>
                </div>

                <div className="leave-info-section">
                  <div className="leave-info-label">Casual Used</div>
                  <div className="leave-info-value gray">{emp.casualUsed} days</div>
                </div>

                <div className="leave-info-section">
                  <div className="leave-info-label">Sick Left</div>
                  <div className={`leave-info-value ${emp.sickLeft > 0 ? 'green' : 'gray'}`}>
                    {emp.sickLeft} days
                  </div>
                </div>

                <div className="leave-info-section">
                  <div className="leave-info-label">Sick Used</div>
                  <div className="leave-info-value gray">{emp.sickUsed} days</div>
                </div>

                {/* ✅ Admin: no apply button */}
              </div>
            ))}
          </div>
        ) : (
          /* ✅ USER VIEW: same as before */
          <div className="leave-list">
            {leaveTypes.map((leave, index) => {
              const booked = getBookedCount(leave.name);
              const available = leave.total - booked;
              return (
                <div className="leave-item" key={index}>
                  <div className="leave-icon-wrapper" style={{ background: leave.color }}>
                    {leave.icon}
                  </div>
                  <div className="leave-name">{leave.name}</div>

                  <div className="leave-info-section">
                    <div className="leave-info-label">Available</div>
                    <div className={`leave-info-value ${available > 0 ? 'green' : 'gray'}`}>
                      {available} days
                    </div>
                  </div>

                  <div className="leave-info-section">
                    <div className="leave-info-label">Booked</div>
                    <div className="leave-info-value gray">{booked} days</div>
                  </div>

                  <button className="apply-button" onClick={() => handleApplyClick(leave.name)}>
                    Apply Leave
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ Modal only for USER */}
      {!isAdmin && showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply {selectedType}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <input
                  type="date"
                  className="form-input"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
                <input
                  type="date"
                  className="form-input"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>

              <textarea
                className="form-textarea"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                placeholder="Reason"
                style={{ marginTop: '10px' }}
              ></textarea>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}